import { Card } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { BookOpen, Clock, FileType, Image, ShieldCheck, CalendarX, Palette, Newspaper, Download } from "lucide-react";
import { currentSkin } from "@/lib/skin";

const GUIDELINES = [
  { icon: Clock, title: "Submission deadlines", body: "Sunday News items are due by 5:00 PM each Wednesday for the following Sunday. Newsletter content is due the 20th of each month. Allow additional lead time for design-heavy requests." },
  { icon: FileType, title: "Approved file types", body: "Images: JPG, PNG, WEBP. Documents: PDF, DOCX, TXT. Maximum upload size is 15 MB per file. Provide the highest resolution available for print materials." },
  { icon: Image, title: "Image guidelines", body: "Use landscape images at least 1200px wide for banners. Ensure you have permission/rights to use any photo. Avoid text baked into images where possible so we can maintain brand consistency." },
  { icon: ShieldCheck, title: "Approval requirements", body: "Items involving finances, partnerships, official positions, or leadership messages require leadership approval before publication. Toggle 'Requires leadership approval' when submitting." },
  { icon: CalendarX, title: "Rush requests & blackout dates", body: "Requests submitted inside the standard production window may be treated as rush and are accommodated when possible. No new promotions are scheduled during the last week of December." },
];

const PRODUCTION = [
  ["Sunday News placement", "2 days"], ["Social-media graphic", "3 days"], ["Email announcement", "3 days"],
  ["Flyer", "5 days"], ["Website update", "4 days"], ["Newsletter article", "7 days"],
  ["Event signage", "7 days"], ["Presentation", "7 days"], ["Printing request", "10 days"], ["Multi-channel campaign", "14 days"],
];

const TEMPLATES = [
  { icon: Newspaper, title: "Sunday News Announcement Template", desc: "Standard format for weekly bulletin items." },
  { icon: Palette, title: "Event Flyer Template", desc: "Brand-approved flyer layout (8.5×11)." },
  { icon: FileType, title: "Newsletter Article Template", desc: "Structure & word-count guidance." },
  { icon: Image, title: "Brand Assets & Logos", desc: "Official logos, colors, and fonts." },
];

export default function Guidelines() {
  const comingSoon = currentSkin() === "aka";
  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground"><BookOpen className="h-5 w-5" /></div>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Guidelines & Brand Resources</h1>
          <p className="text-sm text-muted-foreground">Submission rules, timelines, templates & brand assets.</p>
        </div>
      </div>

      <section>
        <h2 className="font-heading text-xl font-semibold">Submission guidelines</h2>
        <Card className="mt-4 p-2">
          <Accordion type="single" collapsible className="w-full">
            {GUIDELINES.map((g, i) => (
              <AccordionItem key={g.title} value={`g-${i}`} data-testid={`guideline-${i}`}>
                <AccordionTrigger className="px-4 hover:no-underline">
                  <span className="flex items-center gap-3 text-left"><g.icon className="h-4 w-4 text-primary" /> {g.title}</span>
                </AccordionTrigger>
                <AccordionContent className="px-4 text-sm text-muted-foreground">{g.body}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Card>
      </section>

      <section>
        <h2 className="font-heading text-xl font-semibold">Production timelines</h2>
        <Card className="mt-4 divide-y divide-border p-0">
          {PRODUCTION.map(([type, time]) => (
            <div key={type} className="flex items-center justify-between px-5 py-3 text-sm">
              <span>{type}</span><span className="font-semibold text-primary">{time}</span>
            </div>
          ))}
        </Card>
      </section>

      <section>
        <div className="flex items-center gap-2">
          <h2 className="font-heading text-xl font-semibold">Templates & brand resources</h2>
          {comingSoon && <span className="rounded-full bg-accent/10 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-accent" data-testid="templates-coming-soon">Coming soon</span>}
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {TEMPLATES.map((t, i) => (
            <Card key={t.title} className={`flex items-center gap-4 p-5 ${comingSoon ? "opacity-60" : "transition-transform hover:-translate-y-1 hover:shadow-md"}`} data-testid={`template-${i}`}>
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 text-accent"><t.icon className="h-5 w-5" /></div>
              <div className="min-w-0 flex-1">
                <p className="font-heading font-semibold">{t.title}</p>
                <p className="text-xs text-muted-foreground">{t.desc}</p>
              </div>
              {comingSoon
                ? <span className="text-xs font-medium text-muted-foreground">Coming soon</span>
                : <Download className="h-4 w-4 text-muted-foreground" />}
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
